import React, { useState, useEffect } from "react";
import { industryOptions, companySizeOptions } from "../utils/optionLists";
import Input from "../../Input/Input";

/**
 * Contact information section for forms with optional anonymous participation
 */
const ContactInformationSection = ({ leadContactType, setInputRef, enableAnonymousOption = false }) => {
	const [isAnonymous, setIsAnonymous] = useState(false);

	// Handler for anonymity selection change
	const handleAnonymityChange = (e) => {
		setIsAnonymous(e.target.value === "anonymous");
	};

	// Effect to reset input values when switching to anonymous mode
	useEffect(() => {
		if (isAnonymous) {
			// Reset contact field values when switching to anonymous
			const contactFields = document.querySelectorAll('.contact-information input:not([name="is_anonymous"]), .contact-information select');
			contactFields.forEach((field) => {
				field.value = "";
			});
		}
	}, [isAnonymous]);

	// Effekt, um Änderungen bei allen Input-Feldern zu überwachen und die Validierung zu aktualisieren
	useEffect(() => {
		const contactSection = document.querySelector(".contact-information");
		if (!contactSection) return;

		const handleContactInputChange = () => {
			const container = contactSection.closest("[data-step]") || contactSection;
			const allRequiredInputs = contactSection.querySelectorAll("input[required], select[required], textarea[required]");

			// Prüfen, ob alle Pflichtfelder ausgefüllt sind
			let allValid = true;

			allRequiredInputs.forEach((input) => {
				if (!input.value) {
					allValid = false;
				}
			});

			// Fehlerklasse entfernen, wenn alle Felder gültig sind
			if (allValid) {
				container.classList.remove("step-has-error");
			}
		};

		// Event-Listener für Form-Änderungen
		const inputs = contactSection.querySelectorAll("input, select, textarea");
		inputs.forEach((input) => {
			input.addEventListener("change", handleContactInputChange);
			input.addEventListener("input", handleContactInputChange);
		});

		return () => {
			// Cleanup
			inputs.forEach((input) => {
				input.removeEventListener("change", handleContactInputChange);
				input.removeEventListener("input", handleContactInputChange);
			});
		};
	}, []);

	// Contact fields mapping based on leadContactType
	// 1: No contact info (not used here)
	// 2: Email only
	// 3: Email + Name
	// 4: Full contact (Email, Name, Phone, Company)

	// Return early if no contact info required
	if (leadContactType === 1) {
		return null;
	}

	return (
		<div className="contact-information">
            
            <h5 className="mb-4">Kontaktdaten</h5>
			{/* Anonymity selection field - only shown when enabled */}
			{enableAnonymousOption && (
				<div className="row mb-4">
					<div className="col-md-6">
						<Input
							type="select"
							id="contact-anonymity"
							name="is_anonymous"
							label="Kontaktdaten"
							onChange={handleAnonymityChange}
							value={isAnonymous ? "anonymous" : "with-contact"}
							options={[
								{ value: "with-contact", label: "Mit Kontaktdaten teilnehmen" },
								{ value: "anonymous", label: "Anonym teilnehmen" }
							]}
							serializeInTopLevel={true}
							aria-label="Anonymität auswählen"
						/>
						<div className="form-text">
							{isAnonymous 
								? "Sie nehmen anonym teil. Wir werden Sie nicht kontaktieren können." 
								: "Ihre Kontaktdaten werden für eventuelle Rückfragen verwendet."}
						</div>
					</div>
				</div>
			)}

			{/* Contact fields - hidden when anonymous option is enabled and selected */}
			{(!enableAnonymousOption || !isAnonymous) && (
				<>
					<div className="row">
						<div className="col-md-6 mb-3">
							<Input
								type="text"
								label="Vorname"
								required={true}
								name="first_name"
								serializeInTopLevel
								ref={(ref) => setInputRef && setInputRef("first_name", ref)}
							/>
						</div>
						<div className="col-md-6 mb-3">
							<Input
								type="text"
								label="Nachname"
								required={true}
								name="last_name"
								serializeInTopLevel
								ref={(ref) => setInputRef && setInputRef("last_name", ref)}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-md-6 mb-3">
							<Input
								type="email"
								label="E-Mail-Adresse"
								required={true}
								name="email_address"
								serializeInTopLevel
								ref={(ref) => setInputRef && setInputRef("email_address", ref)}
							/>
						</div>
						<div className="col-md-6 mb-3">
							<Input
								type="tel"
								label="Telefonnummer"
								required={false}
								name="mobile"
								serializeInTopLevel
								ref={(ref) => setInputRef && setInputRef("mobile", ref)}
							/>
						</div>
					</div>

					{/* Unternehmensinformationen, wenn leadContactType === 2 */}
					{leadContactType === 2 && (
						<>
							<div className="row">
								<div className="col-md-12 mb-3">
									<Input
										type="text"
										label="Unternehmen"
										required={true}
										name="company_name"
										serializeInTopLevel
										ref={(ref) => setInputRef && setInputRef("company_name", ref)}
									/>
								</div>
							</div>
							<div className="row">
								<div className="col-md-6 mb-3">
									<Input
										type="select"
										label="Unternehmensgröße"
										required={true}
										name="company_size"
										serializeInTopLevel
										options={companySizeOptions}
										ref={(ref) => setInputRef && setInputRef("company_size", ref)}
										searchable={false}
									/>
								</div>
								<div className="col-md-6 mb-3">
									<Input
										type="select"
										label="Branche"
										required={true}
										name="company_branch"
										serializeInTopLevel
										options={industryOptions}
										ref={(ref) => setInputRef && setInputRef("company_branch", ref)}
										searchable={true}
									/>
								</div>
							</div>
							<div className="row">
								<div className="col-md-12 mb-3">
									<Input
										type="text"
										label="Position im Unternehmen"
										required={false}
										name="company_position"
										serializeInTopLevel
										ref={(ref) => setInputRef && setInputRef("company_position", ref)}
									/>
								</div>
							</div>
						</>
					)}
				</>
			)}

			{/* Hidden fields for anonymous submission when anonymous is selected */}
			{enableAnonymousOption && isAnonymous && (
				<div className="d-none">
					<input
						type="hidden"
						name="first_name"
						value=""
						data-serialize-in-top-level="true"
					/>
					<input
						type="hidden"
						name="last_name"
						value=""
						data-serialize-in-top-level="true"
					/>
					<input
						type="hidden"
						name="email_address"
						value=""
						data-serialize-in-top-level="true"
					/>
					<input
						type="hidden"
						name="mobile"
						value=""
						data-serialize-in-top-level="true"
					/>

					{leadContactType === 4 && (
						<>
							<input
								type="hidden"
								name="company_name"
								value=""
								data-serialize-in-top-level="true"
							/>
							<input
								type="hidden"
								name="company_size"
								value=""
								data-serialize-in-top-level="true"
							/>
							<input
								type="hidden"
								name="company_branch"
								value=""
								data-serialize-in-top-level="true"
							/>
							<input
								type="hidden"
								name="company_position"
								value=""
								data-serialize-in-top-level="true"
							/>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default ContactInformationSection;
